import {
  CURRENCY_BY_MARKET,
  type Currency,
  type Market,
  type Merchant,
  type Placement,
  type PlacementSlot,
  type PlusStatus,
  type ReferralRewardStatus,
  type User,
} from '../dataProvider/types';

/**
 * The member's-eye RECONSTRUCTION (D-impersonation). The ONE place the subject's data is
 * shaped into "what the member would see", the tracking-diagnostics / fraud-evidence
 * assembled-facts idiom, writing NOTHING.
 *
 * STRICTLY the MEMBER-FACING PROJECTION. Every field is the member's own view: the FLOORED
 * balance (never the signed one), the cashback list with NO platform margin (the existing
 * no-margin `cashbacks` read model), the member's cashouts, Plus, referrals and market
 * offers. NOTHING admin-only crosses the boundary: no balanceSigned, no commissionAmount /
 * margin, no fraud / risk fields, no admin statuses. An agent who needs the admin truth
 * uses UserShow or the tracking diagnostics (the correct separation).
 *
 * It is honestly a reconstruction of "the member's data as we hold it", NOT a pixel-for-
 * pixel claim to be the member's actual app screen (the tracking-diagnostics honesty
 * posture).
 */

export interface MemberEyeCashback {
  id: number;
  storeName: string;
  grossAmount: number;
  cashbackAmount: number;
  currency: Currency;
  status: string;
  occurredAt: string;
}

export interface MemberEyeCashout {
  id: number;
  amount: number;
  currency: Currency;
  destinationType: string;
  status: string;
  requestedAt: string;
  charityName: string | null;
}

export interface MemberEyeOffer {
  id: number;
  merchantName: string;
  slot: PlacementSlot;
}

export interface MemberEyeView {
  member: { id: number; name: string; email: string; market: Market };
  /** The FLOORED member-facing balance (user.balance), never balanceSigned. */
  balance: number;
  currency: Currency;
  cashbacks: MemberEyeCashback[];
  cashouts: MemberEyeCashout[];
  plus: {
    status: PlusStatus;
    currentPeriodEnd: string | null;
    autoRenew: boolean;
  };
  referrals: {
    referredBy: number | null;
    rewardStatus: ReferralRewardStatus | null;
  };
  offers: MemberEyeOffer[];
}

/** A `cashbacks` read-model row (already scrubbed of margin by the provider). */
export interface CashbackRow {
  id: number;
  storeName: string;
  grossAmount: number;
  cashbackAmount: number;
  currency: Currency;
  status: string;
  occurredAt: string;
}

/** A `cashouts` read-model row (already scrubbed of payout details by the provider). */
export interface CashoutRow {
  id: number;
  amount: number;
  currency: Currency;
  destinationType: string;
  status: string;
  requestedAt: string;
  charityName: string | null;
}

export interface MemberEyeInput {
  user: User;
  cashbacks: CashbackRow[];
  cashouts: CashoutRow[];
  placements: Placement[];
  merchants: Merchant[];
}

const newest = (a: string, b: string): number => b.localeCompare(a);

export function buildMemberEyeView(input: MemberEyeInput): MemberEyeView {
  const { user } = input;
  const merchantName = new Map(input.merchants.map((m) => [m.id, m.name]));

  return {
    member: {
      id: user.id,
      name: user.name,
      email: user.email,
      market: user.market,
    },
    // The member-facing FLOORED balance. A negative signed balance shows as 0 here.
    balance: Number(user.balance ?? 0),
    currency: CURRENCY_BY_MARKET[user.market],
    cashbacks: [...input.cashbacks]
      .sort((a, b) => newest(a.occurredAt, b.occurredAt))
      .map((c) => ({
        id: c.id,
        storeName: c.storeName,
        grossAmount: c.grossAmount,
        cashbackAmount: c.cashbackAmount,
        currency: c.currency,
        status: c.status,
        occurredAt: c.occurredAt,
      })),
    cashouts: [...input.cashouts]
      .sort((a, b) => newest(a.requestedAt, b.requestedAt))
      .map((c) => ({
        id: c.id,
        amount: c.amount,
        currency: c.currency,
        destinationType: c.destinationType,
        status: c.status,
        requestedAt: c.requestedAt,
        charityName: c.charityName,
      })),
    plus: {
      status: user.plusStatus,
      currentPeriodEnd: user.currentPeriodEnd,
      autoRenew: user.autoRenew,
    },
    referrals: {
      referredBy: user.referredBy,
      rewardStatus: user.referralRewardStatus,
    },
    offers: input.placements
      .filter((p) => p.market === user.market)
      .map((p) => ({
        id: p.id,
        merchantName: merchantName.get(p.merchantId) ?? '',
        slot: p.slot,
      })),
  };
}
